import { Fragment, useEffect, useRef, useState } from 'react'
import { Download, FileUp, Loader2, Upload } from 'lucide-react'
import { apiClient } from '@/services/apiClient'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import {
  applyImport,
  exportArchive,
  previewExport,
  previewImport,
  type ExportEntityType,
  type Finding,
  type ImportApplyResult,
  type ImportChoices,
  type ImportPreview,
  type PersonaTargetChoice,
  type PlanItem,
  type UnresolvedCredentialSet,
} from '@/services/portability'
import { PersonaTargetControl } from './PersonaTargetControl'
import {
  BUNDLE_CHANGES,
  BUNDLE_SAME,
  CREDENTIAL_HEADING,
  CREDENTIAL_HINT,
  LEAVE_UNMAPPED,
  bindingsLine,
  credentialSelectLabel,
  errorText,
} from './portabilityText'

interface AdminUser {
  id: string
  email: string
  display_name: string
  role: string
}

type BusyOp = 'export' | 'preview' | 'apply'

const severityClasses: Record<string, string> = {
  error: 'text-destructive',
  warning: 'text-warning-strong',
  info: 'text-muted-foreground',
}

function FindingsList({ findings }: { findings: Finding[] }) {
  if (findings.length === 0) return null
  return (
    <ul className="space-y-1">
      {findings.map((f, i) => (
        <li key={i} className={severityClasses[f.severity] ?? ''}>
          [{f.severity}] {f.message}
        </li>
      ))}
    </ul>
  )
}

/** The choice shown for a persona row: the person's own, else what the server chose. */
function shownChoice(item: PlanItem, overrides: Record<string, PersonaTargetChoice>) {
  return overrides[item.natural_key] ?? item.persona_target?.chosen
}

function sameChoice(a: PersonaTargetChoice, b: PersonaTargetChoice): boolean {
  return a.action === b.action && (a.persona_id ?? null) === (b.persona_id ?? null)
}

/**
 * The rows the person changed from the server's default, plus the names they mapped, or nothing
 * to send. An untouched row is left to the server, which works its default out fresh: after an
 * apply that created a persona, the default moves (Create is no longer allowed), and resending the
 * old shown choice would be refused.
 */
function buildChoices(
  preview: ImportPreview,
  overrides: Record<string, PersonaTargetChoice>,
  credentialMap: Record<string, string>,
): ImportChoices | undefined {
  const personaTargets: Record<string, PersonaTargetChoice> = {}
  for (const item of preview.plan) {
    const choice = overrides[item.natural_key]
    const served = item.persona_target?.default
    if (choice && served && !sameChoice(choice, served)) {
      personaTargets[item.natural_key] = { action: choice.action, persona_id: choice.persona_id ?? null }
    }
  }
  const out: ImportChoices = {}
  if (Object.keys(personaTargets).length > 0) out.personaTargets = personaTargets
  if (Object.keys(credentialMap).length > 0) out.credentialSetMap = credentialMap
  return Object.keys(out).length > 0 ? out : undefined
}

function actionWords(item: PlanItem, choice: PersonaTargetChoice | undefined): string {
  if (choice?.action === 'update') {
    if (item.action === 'update') return BUNDLE_CHANGES
    if (item.action === 'unchanged') return BUNDLE_SAME
  }
  return item.action
}

export function PortabilityPage() {
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [targetOwnerId, setTargetOwnerId] = useState('')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [applied, setApplied] = useState<ImportApplyResult | null>(null)
  const [busyOp, setBusyOp] = useState<BusyOp | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [exportProducts, setExportProducts] = useState(true)
  const [exportWorkflows, setExportWorkflows] = useState(false)
  const [exportPersonas, setExportPersonas] = useState(false)
  const [exportAssets, setExportAssets] = useState(false)
  const [exportFindings, setExportFindings] = useState<Finding[] | null>(null)
  const [overrides, setOverrides] = useState<Record<string, PersonaTargetChoice>>({})
  const [credentialMap, setCredentialMap] = useState<Record<string, string>>({})
  // A mapped name drops out of the server's list, so the page keeps every name it has seen.
  const [credentialSeen, setCredentialSeen] = useState<Record<string, UnresolvedCredentialSet>>({})
  const [refreshing, setRefreshing] = useState(false)
  const previewSeq = useRef(0)
  const busy = busyOp !== null

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await apiClient.fetch('/api/admin/users')
        if (!res.ok) return
        const data: AdminUser[] = await res.json()
        if (!cancelled) setUsers(data)
      } catch {
        // Picker degrades to the default email match; free entry still works server-side.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /** A refusal names the persona when this preview knows the key; else the key itself. */
  const describe = (e: unknown, plan: PlanItem[] | undefined) =>
    errorText(e, (key) => plan?.find((p) => p.natural_key === key)?.persona_target?.name)

  const run = async (op: BusyOp, fn: () => Promise<void>) => {
    setBusyOp(op)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(describe(e, preview?.plan))
    } finally {
      setBusyOp(null)
    }
  }

  const onExport = () =>
    run('export', async () => {
      setExportFindings(null)
      const entityTypes: ExportEntityType[] = [
        ...(exportProducts ? (['product'] as const) : []),
        ...(exportWorkflows ? (['workflow'] as const) : []),
        ...(exportPersonas ? (['persona'] as const) : []),
        ...(exportAssets ? (['asset'] as const) : []),
      ]
      const pre = await previewExport(entityTypes)
      setExportFindings(pre.findings)
      const blob = await exportArchive(entityTypes)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'portability-export.zip'
      a.click()
      URL.revokeObjectURL(url)
    })

  const acceptPreview = (next: ImportPreview) => {
    setPreview(next)
    setCredentialSeen((prev) => {
      const merged = { ...prev }
      for (const set of next.credential_sets ?? []) merged[set.name] = set
      return merged
    })
  }

  const forgetPreview = () => {
    previewSeq.current += 1
    setRefreshing(false)
    setPreview(null)
    setApplied(null)
    setOverrides({})
    setCredentialMap({})
    setCredentialSeen({})
  }

  const onPreview = () =>
    run('preview', async () => {
      if (!file) return
      const seq = ++previewSeq.current
      // a refresh in flight is now stale and will not clear its own flag
      setRefreshing(false)
      setApplied(null)
      setOverrides({})
      setCredentialMap({})
      setCredentialSeen({})
      const next = await previewImport(file, targetOwnerId.trim() || undefined)
      if (seq !== previewSeq.current) return
      acceptPreview(next)
    })

  // A change re-previews with every choice shown; a later answer wins, a stale one is dropped.
  const refreshPreview = async (
    base: ImportPreview,
    nextOverrides: Record<string, PersonaTargetChoice>,
    nextMap: Record<string, string>,
  ) => {
    if (!file) return
    const seq = ++previewSeq.current
    const owner = targetOwnerId.trim() || undefined
    const opts = buildChoices(base, nextOverrides, nextMap)
    setRefreshing(true)
    setError(null)
    try {
      const next = await (opts ? previewImport(file, owner, opts) : previewImport(file, owner))
      if (seq !== previewSeq.current) return
      acceptPreview(next)
    } catch (e) {
      if (seq !== previewSeq.current) return
      setError(describe(e, base.plan))
    } finally {
      if (seq === previewSeq.current) setRefreshing(false)
    }
  }

  const onTargetChange = (key: string, choice: PersonaTargetChoice) => {
    if (!preview) return
    const next = { ...overrides, [key]: choice }
    setOverrides(next)
    void refreshPreview(preview, next, credentialMap)
  }

  const onMapChange = (name: string, setId: string) => {
    if (!preview) return
    const next = { ...credentialMap }
    if (setId) next[name] = setId
    else delete next[name]
    setCredentialMap(next)
    void refreshPreview(preview, overrides, next)
  }

  const onApply = () =>
    run('apply', async () => {
      if (!file) return
      const opts = preview ? buildChoices(preview, overrides, credentialMap) : undefined
      const result = await applyImport(file, targetOwnerId.trim() || preview?.target_owner?.id, opts)
      setApplied(result)
      if (!preview) return
      // What was applied is now this environment's state: the person's choices and map are spent,
      // and the server works the defaults out afresh. The result stays on screen.
      setOverrides({})
      setCredentialMap({})
      setCredentialSeen({})
      void refreshPreview(preview, {}, {})
    })

  const planHasProducts = preview?.plan.some((p) => p.entity_type === 'product') ?? false

  return (
    <PageFrame width="form" className="space-y-8">
      <PageHeader title="Configuration portability" />

      <section className="rounded-lg border border-border p-5">
        <h2 className="mb-2 font-medium text-foreground">Export</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Download this environment&apos;s configuration as a portable archive:
          the product catalog (definitions and images), workflow configuration
          (custom workflows and configuration overlays), personas, and/or the
          asset library. Secrets are never included.
        </p>
        <div className="mb-4 flex flex-wrap gap-4 text-sm text-foreground">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={exportProducts}
              onChange={(e) => setExportProducts(e.target.checked)}
            />
            Product catalog
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={exportWorkflows}
              onChange={(e) => setExportWorkflows(e.target.checked)}
            />
            Workflow configuration
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={exportPersonas}
              onChange={(e) => setExportPersonas(e.target.checked)}
            />
            Personas
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={exportAssets}
              onChange={(e) => setExportAssets(e.target.checked)}
            />
            Asset library
          </label>
        </div>
        <button
          onClick={onExport}
          disabled={
            busy || (!exportProducts && !exportWorkflows && !exportPersonas && !exportAssets)
          }
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busyOp === 'export' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Exporting…
            </>
          ) : (
            <>
              <Download className="h-4 w-4" /> Export archive
            </>
          )}
        </button>
        {exportFindings && (
          <div className="mt-4">
            <FindingsList findings={exportFindings} />
          </div>
        )}
      </section>

      <section className="rounded-lg border border-border p-5">
        <h2 className="mb-2 font-medium text-foreground">Import</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Upload an archive exported from another environment. Preview shows what would
          change; nothing is written until you apply. Import never deletes anything.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            data-testid="portability-file-input"
            ref={fileRef}
            type="file"
            accept=".zip"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null)
              forgetPreview()
            }}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            <FileUp className="h-4 w-4" /> Choose archive…
          </button>
          <span className="text-sm text-muted-foreground">
            {file ? file.name : 'No file selected'}
          </span>
          <select
            aria-label="Target owner"
            value={targetOwnerId}
            onChange={(e) => {
              setTargetOwnerId(e.target.value)
              forgetPreview()
            }}
            disabled={busy}
            className="w-80 rounded-lg border border-border px-3 py-1.5 text-sm bg-card text-foreground disabled:opacity-50"
          >
            <option value="">Target owner: match archive email (default)</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
                {u.role === 'admin' ? ' (admin)' : ''}
              </option>
            ))}
          </select>
          <button
            onClick={onPreview}
            disabled={busy || !file}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            {busyOp === 'preview' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Previewing…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" /> Preview import
              </>
            )}
          </button>
          <button
            onClick={onApply}
            disabled={
              busy || refreshing || !file || !preview || (planHasProducts && !preview.target_owner)
            }
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {busyOp === 'apply' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Applying…
              </>
            ) : (
              'Apply import'
            )}
          </button>
        </div>

        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

        {preview && (
          <div className="mt-5 space-y-3 text-sm">
            <p className="text-foreground">
              Source: <span className="font-medium">{preview.manifest.source_label || 'unknown'}</span>{' '}
              · Owner: <span className="font-medium">{preview.manifest.owner_email}</span>{' '}
              {preview.target_owner
                ? `→ imports as ${preview.target_owner.email} (${preview.target_owner.matched_by})`
                : '→ no matching user in this environment'}
            </p>
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="py-1 pr-4 font-medium">Entity</th>
                  <th className="py-1 pr-4 font-medium">Name</th>
                  <th className="py-1 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {preview.plan.map((item) => {
                  const choice = shownChoice(item, overrides)
                  return (
                    <Fragment key={`${item.entity_type}:${item.natural_key}`}>
                      <tr className={item.persona_target ? '' : 'border-b border-border'}>
                        <td className="py-1 pr-4 text-muted-foreground">{item.entity_type}</td>
                        <td className="py-1 pr-4 text-foreground">
                          {item.persona_target?.name ? (
                            <>
                              <span className="block">{item.persona_target.name}</span>
                              <span className="block text-xs text-muted-foreground">
                                {item.natural_key}
                              </span>
                            </>
                          ) : (
                            item.natural_key
                          )}
                        </td>
                        <td className="py-1">{actionWords(item, choice)}</td>
                      </tr>
                      {item.persona_target && choice && (
                        <tr className="border-b border-border">
                          <td colSpan={3} className="pb-2">
                            <PersonaTargetControl
                              naturalKey={item.natural_key}
                              target={item.persona_target}
                              choice={choice}
                              onChange={(c) => onTargetChange(item.natural_key, c)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
            {Object.keys(credentialSeen).length > 0 && (
              <div className="space-y-2">
                <h3 className="font-medium text-foreground">{CREDENTIAL_HEADING}</h3>
                <p className="text-muted-foreground">{CREDENTIAL_HINT}</p>
                {Object.values(credentialSeen).map((set) => (
                  <div key={set.name} className="flex flex-wrap items-center gap-3">
                    <span className="text-foreground">
                      {bindingsLine(set.binding_count, set.name)}
                    </span>
                    <select
                      aria-label={credentialSelectLabel(set.name)}
                      value={credentialMap[set.name] ?? ''}
                      onChange={(e) => onMapChange(set.name, e.target.value)}
                      className="w-64 rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-foreground"
                    >
                      <option value="">{LEAVE_UNMAPPED}</option>
                      {set.options.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
            <FindingsList findings={preview.findings} />
          </div>
        )}

        {applied && (
          <div className="mt-4 space-y-3 text-sm">
            <ul className="space-y-1">
              {applied.results.map((r) => (
                <li key={`${r.entity_type}:${r.natural_key}`} className="text-foreground">
                  {r.natural_key}: <span className="font-medium">{r.action}</span>
                  {r.detail ? ` — ${r.detail}` : ''}
                </li>
              ))}
            </ul>
            <FindingsList findings={applied.findings} />
          </div>
        )}
      </section>
    </PageFrame>
  )
}
